import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { TrophyIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { trackRecordAPI } from '../services/api'
import { trackRecordHref } from './trackRecord/recordSource'

/** How long the banner stays up after the milestone. */
export const MILESTONE_BANNER_DAYS = 3
const WINDOW_MS = MILESTONE_BANNER_DAYS * 24 * 60 * 60 * 1000
const CONFETTI_MS = 2000

// Per browser, keyed by the milestone's date, so a later milestone starts fresh.
export const dismissedKey = (reachedAt: string) => `fincreator.phase2Milestone.dismissed.${reachedAt}`
export const confettiKey = (reachedAt: string) => `fincreator.phase2Milestone.confetti.${reachedAt}`

const readFlag = (key: string) => {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const writeFlag = (key: string) => {
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    // Storage blocked: the banner still works, it just won't remember.
  }
}

// Unknown counts as reduced: when in doubt, no motion.
const prefersReducedMotion = () => {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? true
  } catch {
    return true
  }
}

const CONFETTI_COLOURS = ['#fbbf24', '#38bdf8', '#34d399', '#f472b6', '#a78bfa']

/** A ~2 s confetti burst on a canvas laid over the banner. Plain canvas, no library. */
const burst = (canvas: HTMLCanvasElement): (() => void) => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}
  const { width, height } = canvas.getBoundingClientRect()
  canvas.width = width
  canvas.height = height
  const pieces = Array.from({ length: 80 }, () => ({
    x: width * (0.1 + Math.random() * 0.8),
    y: height * 0.6,
    vx: (Math.random() - 0.5) * 6,
    vy: -(2 + Math.random() * 5),
    size: 4 + Math.random() * 4,
    spin: Math.random() * Math.PI,
    colour: CONFETTI_COLOURS[Math.floor(Math.random() * CONFETTI_COLOURS.length)],
  }))
  const start = performance.now()
  let frame = 0
  const draw = (now: number) => {
    const elapsed = now - start
    ctx.clearRect(0, 0, width, height)
    if (elapsed >= CONFETTI_MS) return
    ctx.globalAlpha = 1 - elapsed / CONFETTI_MS
    for (const p of pieces) {
      p.vy += 0.15
      p.x += p.vx
      p.y += p.vy
      p.spin += 0.2
      ctx.fillStyle = p.colour
      ctx.save()
      ctx.translate(p.x, p.y)
      ctx.rotate(p.spin)
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
      ctx.restore()
    }
    frame = requestAnimationFrame(draw)
  }
  frame = requestAnimationFrame(draw)
  return () => cancelAnimationFrame(frame)
}

interface Milestone {
  reachedAt: string
  threshold: number
}

/**
 * "N signals closed" banner: up for MILESTONE_BANNER_DAYS after the engine's
 * usable Phase 2 count reaches its threshold (phase2_progress.
 * milestone_reached_at), then gone on its own. Dismissable per browser, with
 * a one-time confetti burst unless the reader prefers reduced motion.
 */
const Phase2MilestoneBanner = () => {
  const [milestone, setMilestone] = useState<Milestone | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [dismissed, setDismissed] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    let cancelled = false
    // Started inside the chain, so even a throw on the call is just "no banner".
    Promise.resolve()
      .then(() => trackRecordAPI.getSignalSummary(false))
      .then((summary) => {
        const progress = summary?.phase2_progress
        if (cancelled || !progress?.milestone_reached_at) return
        setMilestone({ reachedAt: progress.milestone_reached_at, threshold: progress.threshold })
        setDismissed(readFlag(dismissedKey(progress.milestone_reached_at)))
      })
      .catch(() => {
        // No summary, no banner: it is a celebration, not a status.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const endsAt = milestone ? Date.parse(milestone.reachedAt) + WINDOW_MS : NaN
  const visible = Boolean(milestone) && !dismissed && now < endsAt

  // Take the banner down when the window closes, even on a page left open.
  useEffect(() => {
    if (!visible) return
    const timer = window.setTimeout(() => setNow(Date.now()), endsAt - now)
    return () => window.clearTimeout(timer)
  }, [visible, endsAt, now])

  useEffect(() => {
    if (!visible || !milestone || !canvasRef.current) return
    const key = confettiKey(milestone.reachedAt)
    if (readFlag(key) || prefersReducedMotion()) return
    writeFlag(key)
    // Decoration only: a canvas failure must never take the app down with it.
    try {
      return burst(canvasRef.current)
    } catch {
      return undefined
    }
  }, [visible, milestone])

  if (!visible || !milestone) return null

  const dismiss = () => {
    writeFlag(dismissedKey(milestone.reachedAt))
    setDismissed(true)
  }

  return (
    <div
      role="status"
      data-testid="phase2-milestone-banner"
      className="relative mb-6 flex items-center gap-4 overflow-hidden rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        data-testid="phase2-milestone-confetti"
        className="pointer-events-none absolute inset-0 h-full w-full"
      />
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
        <TrophyIcon aria-hidden="true" className="h-5 w-5 text-amber-600" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-gray-900">
          {milestone.threshold} signals closed: every win and every loss on the record.
        </p>
        <p className="text-sm text-gray-500">Phase 2 model training begins now.</p>
      </div>
      <Link
        to={trackRecordHref('engine')}
        className="shrink-0 text-sm font-semibold text-primary-600 hover:text-primary-700"
      >
        See the record →
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 text-gray-500 hover:text-gray-700"
      >
        <XMarkIcon aria-hidden="true" className="h-5 w-5" />
      </button>
    </div>
  )
}

export default Phase2MilestoneBanner
